// Default theme token map — EXACT Tailwind classes extracted from cogo-commerce
// Reference files:
//   product-card-ui.tsx, product-grid-ui.tsx, product-detail-ui.tsx,
//   cart-page-ui.tsx, cart-summary-ui.tsx, checkout-ui.tsx, store-header-ui.tsx
import type { TokenMap } from "./token-map"

export const defaultTokenMap: TokenMap = {
  // ---- Text variants ----
  // Extracted from actual cogo-commerce class usage
  text: {
    "heading-xl": "text-3xl font-bold tracking-tight",        // cart-page h1, checkout h1
    "heading-lg": "text-2xl font-bold lg:text-3xl",            // product-detail h1
    "heading-md": "text-xl font-semibold",                     // product-detail h2 "not found"
    "heading-sm": "text-lg font-bold",                         // product-card price
    "body": "font-medium",                                     // product-card h3
    "body-sm": "text-sm",                                      // product-detail rating text, cart-summary labels
    "caption": "text-xs",                                      // product-card category, rating count
    "price": "font-bold",                                      // product-card price span
    "price-lg": "text-3xl font-bold",                          // product-detail price
    "price-strikethrough": "line-through",                     // product-card/detail original price
    "muted": "text-muted-foreground",                          // various muted text
    "truncate": "truncate",                                    // cart-page item name
  },

  // ---- Color tokens ----
  color: {
    "primary": "text-primary",                                 // store-header "store" brand
    "foreground": "text-foreground",                           // hover:text-foreground on links
    "muted": "text-muted-foreground",                          // categories, descriptions, labels
    "destructive": "text-destructive",                         // hover:text-destructive on delete
    "amber": "text-amber-400",                                 // star rating icons
    "white": "text-white",                                     // (available for dark bg)
  },

  // ---- Text size tokens ----
  size: {
    "xs": "text-xs",                                           // product-card category, star rating count
    "sm": "text-sm",                                           // product-detail rating, cart summary
    "base": "text-base",
    "lg": "text-lg",                                           // product-card price
    "xl": "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",                                         // product-detail price
  },

  // ---- Font weight tokens ----
  weight: {
    "normal": "font-normal",
    "medium": "font-medium",                                   // product-card h3, cart-page item name
    "semibold": "font-semibold",                               // card titles, cart item price
    "bold": "font-bold",                                       // prices, headings
  },

  // ---- Spacing tokens ----
  spacing: {
    "xs": "1",                                                 // gap-1
    "sm": "2",                                                 // gap-2, mb-2
    "md": "4",                                                 // p-4, gap-4
    "lg": "6",                                                 // space-y-6, gap-6
    "xl": "8",                                                 // gap-8, py-8
  },

  // ---- Button variants (matching shadcn button cva) ----
  button: {
    "default": "bg-primary text-primary-foreground shadow hover:bg-primary/90",
    "destructive": "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    "outline": "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
    "secondary": "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
    "ghost": "hover:bg-accent hover:text-accent-foreground",
    "link": "text-primary underline-offset-4 hover:underline",
    // Size variants (prefixed with size:)
    "size:default": "h-9 px-4 py-2",
    "size:sm": "h-9 rounded-md px-3 text-xs",
    "size:lg": "h-10 rounded-md px-8",                        // product-detail "Add to Cart", cart "Proceed"
    "size:icon": "h-9 w-9",
    "size:icon-sm": "h-9 w-9",                                // product-card cart btn, cart-page qty btns
  },

  // ---- Badge variants (matching shadcn badge cva) ----
  badge: {
    "default": "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
    "secondary": "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
    "destructive": "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
    "outline": "text-foreground",
  },

  // ---- Container variants ----
  container: {
    "card": "overflow-hidden transition-shadow hover:shadow-lg",  // product-card Card
    "section": "space-y-6",                                      // product-grid wrapper
    "page": "mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8",     // product-detail, cart, checkout page
    "header": "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
    "image": "relative aspect-square overflow-hidden bg-muted",   // product-card/detail image wrapper
  },

  // ---- Layout variants ----
  layout: {
    "flex-row": "flex flex-row",
    "flex-col": "flex flex-col",
    "flex-between": "flex items-center justify-between",         // product-card price row, header
    "flex-center": "flex items-center",                          // rating stars, qty control
    "grid-2": "grid grid-cols-1 gap-6 sm:grid-cols-2",
    "grid-2-lg": "grid gap-8 lg:grid-cols-2",                   // product-detail layout
    "grid-3-lg": "grid gap-8 lg:grid-cols-3",                   // cart-page, checkout layout
    "grid-4": "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4",  // product-grid
    "space-y-3": "space-y-3",                                   // cart-summary
    "space-y-4": "space-y-4",                                   // cart items list
    "space-y-6": "space-y-6",                                   // product-grid wrapper
  },
}
